import moment from 'moment';

/**
 * Returns the Monday–Sunday (finance-week) range containing the given date.
 *
 * @param {Date} d - Any date within the target week.
 * @returns {{start: Date, end: Date}} Monday (start) and Sunday (end) of that week.
 */
const isoWeek = (d) => ({
  start: moment(d).startOf('isoWeek').toDate(),
  end: moment(d).endOf('isoWeek').toDate(),
});

/**
 * Ordered list of selectable date-range presets. Order is fixed and drives the
 * presets rail rendering: This Week (default), Last Week, This Month, Last Month,
 * Year to Date, Custom. Each entry's `range(today)` returns `{ start, end }`, except
 * `custom`, whose `range` is `null` since it has no computed range of its own.
 *
 * This is the set the client demoed and approved; do not add presets here without
 * a product decision behind them.
 *
 * The period key selected here is emitted on confirm and drives which range the
 * Weekly Overview compares against (see app/services/payments/comparison_period.rb):
 * week vs previous week, month vs previous month, while Year to Date and Custom
 * get no comparison at all.
 *
 * @type {Array<{key: string, label: string, range: ((today: Date) => {start: Date, end: Date})|null}>}
 */
export const PRESETS = [
  { key: 'this_week', label: 'This Week', range: (t) => isoWeek(t) },
  { key: 'last_week', label: 'Last Week', range: (t) => isoWeek(moment(t).subtract(1, 'week')) },
  {
    key: 'this_month',
    label: 'This Month',
    range: (t) => ({ start: moment(t).startOf('month').toDate(), end: moment(t).endOf('month').toDate() }),
  },
  {
    key: 'last_month',
    label: 'Last Month',
    range: (t) => {
      const m = moment(t).subtract(1, 'month');
      return { start: m.clone().startOf('month').toDate(), end: m.clone().endOf('month').toDate() };
    },
  },
  {
    key: 'year_to_date',
    label: 'Year to Date',
    range: (t) => ({ start: moment(t).startOf('year').toDate(), end: moment(t).toDate() }),
  },
  { key: 'custom', label: 'Custom', range: null },
];

/** @type {string} Default preset key selected when no confirmed value exists yet. */
export const DEFAULT_PRESET = 'this_week';
